import * as React from "react";
import { inviteAdmin, listAdmins, revokeAdminPassword, setAdminStatus, type AdminAccount } from "@/api/admin";
import { useAction, useAsync } from "@/lib/useAsync";
import { when } from "@/lib/format";
import { useAuth } from "@/auth/AuthProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/input";
import { ConfirmModal, Modal } from "@/components/ui/modal";
import { Notice } from "@/components/ui/feedback";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonRows } from "@/components/ui/skeleton";
import { TBody, TD, TEmpty, TH, THead, TR, TableShell } from "@/components/ui/table";

/**
 * Who may administer this shop.
 *
 * AN INVITATION IS A ROW WITH NO PASSWORD. Nothing is emailed and no password
 * is chosen for anybody: the person passes Cloudflare Access as that address
 * and sets their own. A password therefore never travels between two people,
 * and there is nothing in an inbox to steal. The same mechanism is the password
 * reset -- revoking turns the account back into an invitation.
 *
 * ADDING A ROW HERE DOES NOT GRANT ACCESS. Cloudflare Access keeps its own
 * allowlist and both doors have to agree; an invitation for somebody Access
 * does not know is a row that can never be claimed. That is harmless and
 * visible, which is why the screen says so rather than pretending otherwise.
 */
export function AdminsScreen() {
  const { data, loading, error, reload } = useAsync(() => listAdmins(), []);
  const { state } = useAuth();
  const action = useAction();
  const [inviting, setInviting] = React.useState(false);
  const [confirm, setConfirm] = React.useState<{ kind: "suspend" | "revoke"; admin: AdminAccount } | null>(null);

  const admins = data?.admins ?? [];
  const activeCount = admins.filter((a) => a.status === "active").length;

  async function act(fn: () => Promise<unknown>) {
    const ok = await action.run(fn);
    reload();
    setConfirm(null);
    return ok;
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Administrators" description="Both doors have to agree: this list, and Cloudflare Access.">
        <Button size="sm" onClick={() => setInviting(true)}>
          Invite somebody
        </Button>
      </PageHeader>

      {error && <Notice tone="error">{error}</Notice>}
      {action.error && <Notice tone="error">{action.error}</Notice>}

      <Notice tone="info">
        Adding an address here does not let anybody in on its own — Cloudflare Access has its own allowlist, and a
        person has to be on both.
      </Notice>

      <Card>
        {loading && admins.length === 0 ? (
          <SkeletonRows rows={3} cols={5} />
        ) : (
          <TableShell>
            <THead>
              <TH>Address</TH>
              <TH>Name</TH>
              <TH>State</TH>
              <TH>Last signed in</TH>
              <TH />
            </THead>
            <TBody>
              {admins.length === 0 && <TEmpty colSpan={5}>Nobody yet.</TEmpty>}
              {admins.map((admin) => {
                const isMe = admin.email === state?.email;
                // The server refuses both of these too, and it is the one that
                // decides. Disabling them here is about not offering an action
                // that cannot work -- the message still comes from the worker
                // if a colleague changes something first.
                const lastActive = admin.status === "active" && activeCount <= 1;
                return (
                  <TR key={admin.id}>
                    <TD>
                      <span className="font-medium">{admin.email}</span>
                      {isMe && <span className="ml-2 text-xs text-muted-foreground">you</span>}
                    </TD>
                    <TD className="text-secondary">{admin.name ?? "—"}</TD>
                    <TD>
                      {admin.status === "suspended" ? (
                        <Badge color="red">suspended</Badge>
                      ) : admin.claimed ? (
                        <Badge color="green">active</Badge>
                      ) : (
                        <Badge color="amber">invited</Badge>
                      )}
                    </TD>
                    <TD className="whitespace-nowrap text-muted-foreground">{when(admin.lastLoginAt)}</TD>
                    <TD>
                      <div className="flex justify-end gap-1.5">
                        {admin.status === "active" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={action.busy || lastActive}
                            title={lastActive ? "The only active administrator cannot be suspended" : undefined}
                            onClick={() => setConfirm({ kind: "suspend", admin })}
                          >
                            Suspend
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={action.busy}
                            onClick={() => void act(() => setAdminStatus(admin.id, "active"))}
                          >
                            Reinstate
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={action.busy || isMe || !admin.claimed}
                          title={
                            isMe
                              ? "Change your own password instead — revoking it would let anyone past Access set a new one"
                              : !admin.claimed
                                ? "This account has no password yet"
                                : undefined
                          }
                          onClick={() => setConfirm({ kind: "revoke", admin })}
                        >
                          Revoke password
                        </Button>
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </TableShell>
        )}
      </Card>

      <InviteModal
        open={inviting}
        busy={action.busy}
        onClose={() => setInviting(false)}
        onInvite={async (email, name) => {
          if (await act(() => inviteAdmin(email, name))) setInviting(false);
        }}
      />

      <ConfirmModal
        open={confirm !== null}
        title={confirm?.kind === "revoke" ? "Revoke this password?" : "Suspend this account?"}
        confirmLabel={confirm?.kind === "revoke" ? "Revoke it" : "Suspend"}
        busy={action.busy}
        onClose={() => setConfirm(null)}
        body={
          confirm?.kind === "revoke" ? (
            <>
              <p>
                <span className="font-medium">{confirm.admin.email}</span> will be signed out everywhere and their
                password cleared.
              </p>
              <p className="mt-2">
                Nothing is emailed. They set a new one themselves the next time they come through Cloudflare Access.
              </p>
            </>
          ) : (
            <>
              <p>
                <span className="font-medium">{confirm?.admin.email}</span> will be signed out immediately and will not
                be able to sign in again.
              </p>
              <p className="mt-2">Another administrator can reinstate them at any time.</p>
            </>
          )
        }
        onConfirm={() => {
          if (!confirm) return;
          void act(() =>
            confirm.kind === "revoke"
              ? revokeAdminPassword(confirm.admin.id)
              : setAdminStatus(confirm.admin.id, "suspended"),
          );
        }}
      />
    </div>
  );
}

function InviteModal({
  open,
  busy,
  onClose,
  onInvite,
}: {
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onInvite: (email: string, name: string) => void;
}) {
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setEmail("");
      setName("");
    }
  }, [open]);

  return (
    <Modal
      open={open}
      title="Invite an administrator"
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => onInvite(email, name)} disabled={busy || email.trim().length === 0}>
            {busy ? "Working…" : "Create the invitation"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Address"
          htmlFor="invite-email"
          hint="The same address Cloudflare Access knows them by. They set their own password."
        >
          <Input
            id="invite-email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Name" htmlFor="invite-name" hint="Optional. Shown on this screen only.">
          <Input id="invite-name" autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
